'use client'

/**
 * La **scena di sostituzione** a partire dagli eventi di una sessione.
 *
 * PERCHÉ UN HOOK: la sostituzione non cambia lo stato (`case 'substitution':` nel
 * reducer non tocca né punteggio né servizio), quindi chi la vuole mostrare deve
 * leggere gli **eventi**. La console la disegnava, la pagina della gara anche, la
 * card della lista `/live` no: lo stesso cambio si vedeva in un posto e non
 * nell'altro. Qui la regola sta in un punto solo, e la usano tutte e tre.
 *
 * `ready` è il segnale che la fotografia iniziale degli eventi è arrivata: al
 * primo giro si prende il segnaposto **senza animare**, così aprendo una pagina a
 * metà partita non si mettono in scena i cambi di un'ora fa.
 */
import { useEffect, useRef, useState } from 'react'
import { SUBSTITUTION_TOTAL_MS, type SubstitutionSwap } from '@/lib/live/substitution'
import { parseSubstitutionDetail } from '@/hooks/useLiveSubstitution'

export interface BoardEventRow {
  id: number
  type: string
  team: string | null
  payload: Record<string, unknown> | null
}

export function useSubstitutionScene(events: BoardEventRow[], ready: boolean): SubstitutionSwap | null {
  const [substitution, setSubstitution] = useState<SubstitutionSwap | null>(null)
  const seen = useRef(0)
  const baselineDone = useRef(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!ready) return
    if (!baselineDone.current) {
      baselineDone.current = true
      seen.current = events[events.length - 1]?.id ?? 0
      return
    }
    for (const event of events) {
      if (event.id <= seen.current) continue
      seen.current = event.id
      if (event.type !== 'substitution') continue
      const rawTeam = event.team ?? (typeof event.payload?.team === 'string' ? event.payload.team : null)
      const team = rawTeam === 'home' || rawTeam === 'away' ? rawTeam : null
      if (!team) continue
      const detail = typeof event.payload?.detail === 'string' ? event.payload.detail : null
      const { out, in: incoming } = parseSubstitutionDetail(detail)
      setSubstitution({ team, out, in: incoming })
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => setSubstitution(null), SUBSTITUTION_TOTAL_MS)
    }
  }, [events, ready])

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
  }, [])

  return substitution
}

export default useSubstitutionScene
